<template>
  <div class="auth-wrapper">
    <div v-if="loading">
      <slot name="loading" v-if="$slots.loading"></slot>
      <div v-else class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </div>

    <div v-else-if="!user">
      <slot name="unauthenticated" v-if="$slots.unauthenticated"></slot>
      <div v-else class="auth-container">
        <!-- Fallback to original auth forms if no unauthenticated slot provided -->
        <AuthForms @auth-success="handleAuthSuccess" />
      </div>
    </div>

    <div v-else class="authenticated-content">
      <slot :user="user" :userAttributes="userAttributes" :refreshUserAttributes="refreshUserAttributes" />
    </div>
  </div>
</template>

<script>
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import AuthForms from './AuthForms.vue';
import { getUserName, getUserEmail, getUserPicture } from '../utils/userUtils.js';

export default {
  name: 'AuthWrapper',
  components: {
    AuthForms
  },
  data() {
    return {
      loading: false,
      user: null,
      userAttributes: null,
      showDropdown: false
    }
  },
  async mounted() {
    try {
      // Check auth in background without blocking page load
      this.checkCurrentUser();

      // Listen for auth state changes including hosted UI callback
      this.hubListener = Hub.listen('auth', (data) => {
        const { event } = data.payload;
        if (event === 'signedIn' || event === 'signUp' || event === 'autoSignIn' || event === 'customOAuthState') {
          this.checkCurrentUser();
        } else if (event === 'signedOut') {
          this.user = null;
        }
      });

      // Handle OAuth callback from URL
      await this.handleOAuthCallback();
    } catch (error) {
      console.error('AuthWrapper initialization error:', error);
      // Don't break the app, just log the error
    }
  },
  
  beforeUnmount() {
    // Clean up the Hub listener
    if (this.hubListener) {
      this.hubListener();
    }
  },
  methods: {
    async checkCurrentUser() {
      try {
        this.user = await getCurrentUser();
        // Also fetch and cache user attributes
        if (this.user) {
          this.userAttributes = await fetchUserAttributes();
        }
      } catch (error) {
        this.user = null;
        this.userAttributes = null;
      }
    },

    async refreshUserAttributes() {
      // Method to refresh cached user attributes (called after profile updates)
      if (this.user) {
        try {
          this.userAttributes = await fetchUserAttributes();
        } catch (error) {
          console.error('Error refreshing user attributes:', error);
        }
      }
    },

    async handleAuthSuccess(user) {
      // Refresh user state from Amplify after authentication
      await this.checkCurrentUser();
    },

    async handleOAuthCallback() {
      // Check if we're returning from OAuth
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('code') || window.location.hash.includes('access_token')) {
        try {
          // Wait a moment for Amplify to process the callback
          setTimeout(async () => {
            await this.checkCurrentUser();
            // Clean up the URL params after OAuth callback
            window.history.replaceState({}, document.title, window.location.pathname);
          }, 100);
        } catch (error) {
          console.error('OAuth callback error:', error);
        }
      }
    },

    getUserName() {
      return getUserName(this.user);
    },

    getUserEmail() {
      return getUserEmail(this.user);
    },

    getUserPicture() {
      return getUserPicture(this.user);
    },

    getUserFirstName() {
      // Check for Amplify Gen 2 Google attribute mapping first
      return this.user?.attributes?.givenName || this.user?.attributes?.given_name || '';
    },

    getUserLastName() {
      // Check for Amplify Gen 2 Google attribute mapping first
      return this.user?.attributes?.familyName || this.user?.attributes?.family_name || '';
    },

    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },

    async signOut() {
      try {
        await amplifySignOut();
        this.user = null;
        this.showDropdown = false;
      } catch (error) {
        console.error('Sign out error:', error);
      }
    }
  }
}
</script>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.auth-container {
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
}

.auth-tabs {
  display: flex;
  margin-bottom: 2rem;
  border-bottom: 1px solid #dee2e6;
}

.tab-button {
  flex: 1;
  padding: 1rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  color: #6c757d;
  border-bottom: 2px solid transparent;
}

.tab-button.active {
  color: #007bff;
  border-bottom-color: #007bff;
}

.auth-form {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.confirmation-form {
  background: #fff3cd;
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #ffeaa7;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 1rem;
}

.form-group input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.auth-button {
  width: 100%;
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.auth-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.auth-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
  margin-top: 1rem;
}

.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sign-out-button {
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.sign-out-button:hover {
  background-color: #c82333;
}

.authenticated-content {
  min-height: 100vh;
}

/* Make authenticated header responsive */
.authenticated-content .container {
  width: 100% !important;
  max-width: 1200px !important;
  padding-left: 15px !important;
  padding-right: 15px !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

@media (max-width: 767px) {
  .authenticated-content .navbar-nav {
    margin: 0 !important;
  }
  
  .authenticated-content .navbar-right {
    margin-right: 0 !important;
  }
  
  .authenticated-content .dropdown-toggle {
    white-space: nowrap;
  }
  
  .authenticated-content .profile-avatar {
    display: none;
  }
}
</style>